// Shared widget-config + embed helpers for the marketing-site widget
// tools (builder, Instagram/hashtag feed, social-proof, link-in-bio).
//
// The Idukki widget is GUID-driven: a saved gallery-layout is rendered
// anywhere by `<div data-ugc="idukki" data-guid="...">` + the loader
// script, and all appearance comes from the saved layout (see the
// idukki-ext DisplayWidget). So the marketing site only ever needs to
// (a) let a visitor compose a layout config, (b) show a real preview of
// it, and (c) hand back the embed snippet once a guid exists.
//
// URLs are env-configurable because the loader / preview host differ
// across environments (Idukki CDN vs the Feefo proxy). Defaults point at
// the confirmed-working hosts; the team can override per environment.

export const WIDGET_LOADER_URL =
  process.env.NEXT_PUBLIC_WIDGET_LOADER_URL ?? 'https://app.idukki.io/dist/loader.js'

export const WIDGET_PREVIEW_BASE =
  process.env.NEXT_PUBLIC_WIDGET_PREVIEW_BASE ?? 'https://widgetugc.feefo.com'

export const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? 'https://app.idukki.io'

/** The layout view types a visitor can pick in the builder. Mirrors the
 *  `galleryView` keys the widget renderer understands (see helpers/layoutMap
 *  in nextapp). Kept to the safe, broadly-supported set for the public tool. */
export type WidgetView =
  | 'grid'
  | 'carousel'
  | 'masonry'
  | 'collage'
  | 'stories'
  | 'reelCarousel'
  | 'wall2'

/** A source a visitor wants their UGC pulled from. Drives copy + the
 *  per-channel tool skins; the real connection happens in-app via OAuth. */
export type WidgetSource = 'instagram' | 'tiktok' | 'hashtag' | 'reviews' | 'youtube' | 'upload'

/** The minimal, layout-shaped config the builder composes. This maps 1:1
 *  onto the `layout.galleryInformation` the create-collection API expects,
 *  so the same object can be sent straight to `POST /gallery-layouts`. */
export type WidgetConfig = {
  name: string
  view: WidgetView
  source: WidgetSource
  /** The handle / hashtag / business the visitor typed for their source,
   *  e.g. "@yourbrand" or "#campaign". Captured for the preview + lead so
   *  the app can prefill the connection. Does not affect the layout. */
  sourceHandle?: string
  columns: number
  mobileColumns: number
  spacing: number
  pagination: 'pagination' | 'loadMore' | 'autoScroll' | 'arrows' | 'none'
  accent: string
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  name: 'My UGC gallery',
  view: 'grid',
  source: 'instagram',
  sourceHandle: '',
  columns: 4,
  mobileColumns: 2,
  spacing: 10,
  pagination: 'pagination',
  accent: '#CDEB45',
}

/** Translate the builder config into the `layout` shape the create-collection
 *  API (`POST /gallery-layouts`) accepts. Kept deliberately small: the app
 *  fills the rest of the defaults from its layout map on save. */
export function configToLayout(c: WidgetConfig) {
  return {
    galleryView: c.view,
    galleryInformation: {
      name: c.name,
      postPerPage: '14',
      columns: String(c.columns),
      mobileColumns: String(c.mobileColumns),
      pagination: c.pagination,
      postSpacing: String(c.spacing),
    },
    brandColorFont: { brandAccentColor: c.accent },
  }
}

/** The HTML snippet a customer pastes on their storefront. GUID-only:
 *  every visual choice lives in the saved layout. */
export function buildEmbedSnippet(guid: string, opts?: { bguid?: string; lazy?: boolean }): string {
  const attrs = [
    'data-ugc="idukki"',
    `data-guid="${guid}"`,
    opts?.bguid ? `data-bguid="${opts.bguid}"` : '',
    opts?.lazy ? 'data-ugc-lazy="1"' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return `<div ${attrs}></div>\n<script src="${WIDGET_LOADER_URL}" defer></script>`
}

/** A standalone hosted URL that renders one collection, used for a real
 *  live preview in an iframe without rebuilding the renderer. */
export function hostedPreviewUrl(
  guid: string,
  opts?: { bguid?: string; test?: boolean },
): string {
  const params = new URLSearchParams({ guid })
  if (opts?.bguid) params.set('bguid', opts.bguid)
  if (opts?.test) params.set('idk_env', 'test')
  return `${WIDGET_PREVIEW_BASE}/?${params.toString()}`
}

/** Host for rep-generated sales-proposal widget previews (app/[slug]/
 *  [proposalSlug]). Deliberately a SEPARATE constant from WIDGET_PREVIEW_BASE:
 *  that one defaults to the Feefo white-label host (widgetugc.feefo.com,
 *  used by the marketing-site widget builder's own live preview), whereas a
 *  sales proposal is a generic-brand prospect eval and must render on
 *  Idukki's own generic domain, not a feefo-branded one. Mirrors the
 *  production leg of nextapp's helpers/PreviewUrlHelper.ts hostname
 *  branching (widget.idukki.io); override via env for staging. */
export const PROPOSAL_WIDGET_BASE =
  process.env.NEXT_PUBLIC_PROPOSAL_WIDGET_BASE ?? 'https://widget.idukki.io'

/** Build the live iframe src for one layoutRow on a sales-proposal page.
 *  Same `?guid=&bguid=` query shape as hostedPreviewUrl above, just against
 *  PROPOSAL_WIDGET_BASE instead of WIDGET_PREVIEW_BASE. The widget's
 *  layout/style is resolved server-side per guid (already branded by the
 *  dashboard before the proposal is shared) — no brand params needed here. */
export function proposalPreviewUrl(guid: string, bguid: string): string {
  const params = new URLSearchParams({ guid, bguid })
  return `${PROPOSAL_WIDGET_BASE}/?${params.toString()}`
}

/** Deep link into the app to finish setup (connect a real source, go
 *  live) carrying the builder's intent so the app can prefill. */
export function appSignupUrl(opts?: { source?: WidgetSource; ref?: string }): string {
  const params = new URLSearchParams({ ref: opts?.ref ?? 'marketing-widget-builder' })
  if (opts?.source) params.set('source', opts.source)
  return `${APP_URL}/register?${params.toString()}`
}
